import { createSelector } from 'reselect'
import getCheckoutTravellerSchema, { isCheckoutTravellerReadinessSatisfied } from 'checkout/selectors/getCheckoutTravellerSchema'
import getIsCheckoutTravellerSchemaFetching from 'checkout/selectors/getCheckoutTravellerSchemaFetching'
import { getTravellerFormSchemaRequest, isTravellerFormNeeded } from 'checkout/selectors/request/travellerSchema'
import { selectIsGuestCheckoutMode } from 'selectors/checkoutSelectors'
import {
  isGiftCardItem,
  isLoyaltyPurchasePointsItem,
  isLuxPlusSubscriptionItem,
  isMerchandiseItem,
  isMerchandiseShippingItem,
  isPurchaseCreditItem,
  isSubscriptionJoinItem,
} from 'lib/checkout/checkoutUtils'

function doesCheckoutItemSkipTravellerForms(item: App.Checkout.AnyItem): boolean {
  return isGiftCardItem(item) ||
    isPurchaseCreditItem(item) ||
    isLoyaltyPurchasePointsItem(item) ||
    isLuxPlusSubscriptionItem(item) ||
    isSubscriptionJoinItem(item) ||
    isMerchandiseItem(item) ||
    isMerchandiseShippingItem(item)
}

export const getCartOnlyHasItemsWithoutTravellerForms = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): boolean => {
    return items.length > 0 && items.every(doesCheckoutItemSkipTravellerForms)
  },
)

export const getCheckoutNeedsTravellerForms = createSelector(
  (state: App.State) => isTravellerFormNeeded(state),
  (state: App.State) => getCartOnlyHasItemsWithoutTravellerForms(state),
  (
    travellerFormNeeded,
    cartOnlyHasItemsWithoutTravellerForms,
  ): boolean => {
    return travellerFormNeeded && !cartOnlyHasItemsWithoutTravellerForms
  },
)

export const shouldShowTravellerForm = createSelector(
  (state: App.State) => state.checkout.isCartRestored,
  (state: App.State) => getCheckoutNeedsTravellerForms(state),
  (state: App.State) => getTravellerFormSchemaRequest(state),
  (isCartRestored, checkoutNeedsTravellerForms, formRequest): boolean => {
    return isCartRestored && checkoutNeedsTravellerForms && formRequest.items.length > 0
  },
)

export const getIsCheckoutTravellerReadinessSatisfied = createSelector(
  (state: App.State) => getCheckoutNeedsTravellerForms(state),
  (state: App.State) => shouldShowTravellerForm(state),
  (state: App.State) => getCheckoutTravellerSchema(state),
  (state: App.State) => getIsCheckoutTravellerSchemaFetching(state),
  (state: App.State) => state.checkout.isFormInitialised,
  selectIsGuestCheckoutMode,
  (checkoutNeedsTravellerForms, showTravellerForm, schemaData, isFetchingSchema, isFormInitialised, isGuestCheckoutMode): boolean => {
    return isCheckoutTravellerReadinessSatisfied({
      checkoutNeedsTravellerForms,
      shouldShowTravellerForm: showTravellerForm,
      schemaData,
      isFetchingSchema,
      isFormInitialised,
      isGuestCheckoutMode,
    })
  },
)
